import type {
  AddressAutocompletePrediction,
  AddressObject,
  RideAvailabilityCheckRequestObject,
  RideAvailabilityCheckResponseObject,
  RideCheckoutRequestObject,
  RideCheckoutResponseObject,
  RidePriceCalculatorRequestObject,
  RidePriceCalculatorResponseObject,
  RidePublicConfigResponseObject,
} from '@cfixe-monorepo/shared';
import {
  ProPaymentStatus,
  minorUnitToSiteEventValue,
  public_env,
  type PublicBookingSource,
  resolveSiteEventCurrency,
  toE164IfInternational,
} from '@cfixe-monorepo/shared';
import {
  cancelPreReservation,
  checkRideAvailability,
  computeRidePrice,
  confirmPayOnSitePreReservation,
  createRideCheckout,
  fetchAddressAutocomplete,
  fetchAddressDetails,
  fetchRideConfig,
} from '../../website/api/professional-api';
import { getRequiredCaptchaToken, type CapRef } from '../../website/functions/captcha';
import {
  clearSiteEventReserve,
  takeSiteEventReserve,
  writeSiteEventReserve,
  type ReservedSiteEvent,
} from '../../website/functions/site-event-reserve';
import type { ProPaymentDetails, ProPaymentStatusResponse } from '../../pro-payment';

/**
 * Callbacks injectés dans le RideTunnel (miroir de createGiftCardCallbacks).
 * `onLoadPaymentDetails`/`onPollPaymentStatus` gardent les signatures de GiftCardCallbacks :
 * GiftCardPaymentSection les accepte par typage structurel (paiement inline réutilisé tel quel).
 */
export interface RideCallbacks {
  onLoadConfig: () => Promise<RidePublicConfigResponseObject>;
  /** Estimation serveur (captcha résolu à chaque appel — le token Cap.js est à usage unique). */
  onComputePrice: (
    payload: Omit<RidePriceCalculatorRequestObject, 'professional_slug'>,
  ) => Promise<RidePriceCalculatorResponseObject>;
  onCheckout: (payload: RideCheckoutRequestObject) => Promise<RideCheckoutResponseObject>;
  /** Pré-check consultatif de dispo à la saisie de l'heure (sans captcha, non bloquant). */
  onCheckAvailability: (payload: RideAvailabilityCheckRequestObject) => Promise<RideAvailabilityCheckResponseObject>;
  onAutocompleteAddress: (query: string) => Promise<AddressAutocompletePrediction[]>;
  onResolveAddressDetails: (placeId: string) => Promise<AddressObject>;
  onLoadPaymentDetails: (uuid: string, capToken?: string) => Promise<ProPaymentDetails>;
  onPollPaymentStatus: (uuid: string) => Promise<ProPaymentStatusResponse>;
  /** Politique FULL_PAYMENT_OPTIONAL : confirme la course sans paiement en ligne (annule l'intent Stripe, réservation confirmée). */
  onConfirmPayOnSite: (uuid: string) => Promise<void>;
  /**
   * Annule une course encore en attente de paiement (pro_payment WAITING_PAYMENT) : sans cela elle
   * bloque son véhicule et son chauffeur jusqu'à expiration. Clé = uuid du pro_payment.
   */
  onCancelPendingRide: (paymentUuid: string) => Promise<void>;
}

export interface CreateRideCallbacksOptions {
  /** Invoked once when the ride reservation is confirmed (pay-on-site or online payment settled) — e.g. conversion tracking. */
  onReservationConfirmed?: (uuid: string) => void;
  /**
   * Événement de suivi `booking_confirmed` (`booking_type: 'ride'`) de la course confirmée,
   * rangé en `sessionStorage` au checkout (cf. site-event-reserve.ts). N'est PAS appelé quand la
   * réserve est absente (confirmation rechargée, paiement terminé dans un autre navigateur).
   */
  onBookingTrackingEvent?: (event: ReservedSiteEvent<'booking_confirmed'>) => void;
  /** Canal d'entrée déclaré au backend (`booking_source`) — même contrat que createBookingCallbacks. */
  source?: PublicBookingSource;
  /** Campagne à l'origine de la visite (`?cid=`) — même contrat que createBookingCallbacks. */
  campaign_id?: number;
}

/**
 * Événement de suivi d'une course tout juste créée. `undefined` si le backend n'a pas renvoyé le
 * total : mieux vaut pas d'événement qu'une `value` fausse. Sans formule unique (réservation de
 * plusieurs véhicules de formules différentes), `items` et `service_name` restent vides.
 */
function buildRideTrackingEvent(
  payload: RideCheckoutRequestObject,
  result: RideCheckoutResponseObject,
): ReservedSiteEvent<'booking_confirmed'> | undefined {
  if (typeof result.net_total_raw !== 'number') return undefined;
  const value = minorUnitToSiteEventValue(result.net_total_raw);
  const hasFormula = result.prestation_id !== undefined && !!result.prestation_name;
  return {
    params: {
      transaction_id: result.reservation_uuid,
      value,
      currency: resolveSiteEventCurrency(result.currency),
      amount_paid: minorUnitToSiteEventValue(result.amount_to_pay_today_raw ?? 0),
      booking_type: 'ride',
      service_name: result.prestation_name ?? '',
      items: hasFormula
        ? [
            {
              item_id: String(result.prestation_id),
              item_name: result.prestation_name ?? '',
              price: value,
              quantity: 1,
            },
          ]
        : [],
    },
    userData: {
      email: payload.contact?.email,
      phone_number: toE164IfInternational(payload.contact?.phone_number),
    },
  };
}

export function createRideCallbacks(professionalSlug: string, options?: CreateRideCallbacksOptions): RideCallbacks {
  const captchaRef: CapRef = { current: null };
  /* Uuid déjà reporté à onReservationConfirmed — le poll de statut est répétitif,
     le hook doit partir exactement une fois par course. */
  let confirmedNotifiedUuid: string | undefined;

  /* La réserve de suivi n'est tenue que si un consommateur l'écoute. */
  const tracksBooking = options?.onBookingTrackingEvent !== undefined;

  /**
   * @param nothingChargedOnline la course est confirmée sans débit en ligne (paiement sur place
   *   choisi au dernier moment, carte seulement enregistrée) : `amount_paid` est remis à 0.
   */
  const notifyReservationConfirmed = (uuid: string, nothingChargedOnline = false): void => {
    if (confirmedNotifiedUuid === uuid) return;
    confirmedNotifiedUuid = uuid;
    options?.onReservationConfirmed?.(uuid);
    if (!tracksBooking) return;
    const event = takeSiteEventReserve<'booking_confirmed'>(uuid);
    if (!event) return;
    options?.onBookingTrackingEvent?.(
      nothingChargedOnline ? { ...event, params: { ...event.params, amount_paid: 0 } } : event,
    );
  };

  return {
    onLoadConfig: (): Promise<RidePublicConfigResponseObject> => fetchRideConfig(professionalSlug),
    onComputePrice: async (payload): Promise<RidePriceCalculatorResponseObject> => {
      const token = await getRequiredCaptchaToken(captchaRef);
      return computeRidePrice({ ...payload, professional_slug: professionalSlug }, token);
    },
    onCheckout: async (payload): Promise<RideCheckoutResponseObject> => {
      const token = await getRequiredCaptchaToken(captchaRef);
      const result = await createRideCheckout(
        professionalSlug,
        {
          ...payload,
          ...(options?.source ? { source: options.source } : {}),
          ...(options?.campaign_id ? { campaign_id: options.campaign_id } : {}),
        },
        token,
      );
      /* Rangé sous l'uuid que recevront le poll et la confirmation (celui du paiement s'il y en a un). */
      const trackingEvent = tracksBooking ? buildRideTrackingEvent(payload, result) : undefined;
      if (trackingEvent) writeSiteEventReserve(result.payment_uuid ?? result.reservation_uuid, trackingEvent);
      /* Sans paiement requis (politique FREE, pro sans Stripe Connect), le tunnel affiche son
         succès dès le checkout : c'est la confirmation (miroir du booking gratuit). */
      if (!result.requires_payment || !result.payment_uuid) {
        notifyReservationConfirmed(result.payment_uuid ?? result.reservation_uuid);
      }
      return result;
    },
    onCheckAvailability: (payload): Promise<RideAvailabilityCheckResponseObject> =>
      checkRideAvailability(professionalSlug, payload),
    onAutocompleteAddress: async (query): Promise<AddressAutocompletePrediction[]> => {
      const token = await getRequiredCaptchaToken(captchaRef);
      // Slug du pro → suggestions biaisées (soft) autour de son point de départ VTC (sinon de son
      // adresse). Couvre les deux hôtes du tunnel (/book/[slug] et booking-widget des sites).
      return (await fetchAddressAutocomplete(query, token, professionalSlug)).predictions;
    },
    onResolveAddressDetails: async (placeId): Promise<AddressObject> => {
      const token = await getRequiredCaptchaToken(captchaRef);
      return (await fetchAddressDetails(placeId, token)).address;
    },
    onLoadPaymentDetails: async (uuid: string, capToken?: string): Promise<ProPaymentDetails> => {
      const res = await fetch(`${public_env.API_ENDPOINT}/public/pro-payment/${uuid}`, {
        headers: capToken ? { 'x-cap-token': capToken } : undefined,
      });
      if (!res.ok) throw new Error('pro-payment details failed');
      return (await res.json()) as ProPaymentDetails;
    },
    onPollPaymentStatus: async (uuid: string): Promise<ProPaymentStatusResponse> => {
      const res = await fetch(`${public_env.API_ENDPOINT}/public/pro-payment/${uuid}/status`);
      if (!res.ok) throw new Error('pro-payment status failed');
      const data = (await res.json()) as ProPaymentStatusResponse;
      /* Le paiement en ligne ne passe pas par onConfirmPayOnSite : le hook de
         confirmation part ici, à la première réponse « réglée » du poll. */
      if (data.status === ProPaymentStatus.PAYED || data.status === ProPaymentStatus.AUTHORIZED) {
        notifyReservationConfirmed(uuid, data.status === ProPaymentStatus.AUTHORIZED);
      }
      return data;
    },
    onConfirmPayOnSite: async (uuid: string): Promise<void> => {
      // Endpoint générique (clé = uuid du pro_payment) : la garde backend exige FULL_PAYMENT_OPTIONAL,
      // satisfaite par la row ride (prestation_payment_type snapshoté depuis la prestation « Course »).
      await confirmPayOnSitePreReservation(uuid);
      notifyReservationConfirmed(uuid, true);
    },
    // Endpoint générique de la pré-réservation (clé = uuid du pro_payment) : n'annule qu'un paiement
    // encore WAITING_PAYMENT (et son intent Stripe), libère les rendez-vous de la course.
    onCancelPendingRide: async (paymentUuid: string): Promise<void> => {
      await cancelPreReservation(paymentUuid);
      if (tracksBooking) clearSiteEventReserve(paymentUuid);
    },
  };
}
